import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { MapPin, Plus, Trash2, Eye, Star, CalendarDays, Pencil } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const DEFAULT_IMAGES = [
  "/images/hero.jpg",
];
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TERRAIN_LABELS = { rv: "VR", tent: "Tente", van: "Van", all: "Tous" };

function getImageUrl(s, i) {
  const img = s.photos?.[0] || s.image_url;
  if (!img) return DEFAULT_IMAGES[i % DEFAULT_IMAGES.length];
  if (img.startsWith("/api/")) return `${BACKEND_URL}${img}`;
  return img;
}

export default function MySpacesPage() {
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/spaces/my").then(r => setSpaces(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);

  const toggleAvailability = async (space) => {
    try { await api.put(`/spaces/${space.id}`, { available: !space.available }); setSpaces(prev => prev.map(s => s.id === space.id ? { ...s, available: !s.available } : s)); toast.success(space.available ? "Terrain masque" : "Terrain visible"); }
    catch { toast.error("Erreur"); }
  };
  const deleteSpace = async (id) => {
    if (!window.confirm("Supprimer ce terrain?")) return;
    try { await api.delete(`/spaces/${id}`); setSpaces(prev => prev.filter(s => s.id !== id)); toast.success("Terrain supprime"); }
    catch { toast.error("Erreur"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950" data-testid="my-spaces-title">Mes terrains</h1>
        <Link to="/create-space" data-testid="add-space-btn"><Button className="bg-emerald-700 text-white hover:bg-emerald-800 rounded-xl"><Plus className="h-4 w-4 mr-2" /> Ajouter</Button></Link>
      </div>
      {loading ? <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-4 border-zinc-300 border-t-emerald-700 rounded-full" /></div>
      : spaces.length === 0 ? (
        <div className="text-center py-20" data-testid="no-my-spaces"><MapPin className="h-12 w-12 text-zinc-300 mx-auto mb-4" /><p className="text-zinc-500 text-lg">Aucun terrain publie</p><Link to="/create-space"><Button className="mt-4 bg-emerald-700 text-white hover:bg-emerald-800 rounded-xl">Publier mon premier terrain</Button></Link></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {spaces.map((space, i) => (
            <Card key={space.id} className="border border-zinc-200 rounded-2xl bg-white overflow-hidden" data-testid={`my-space-${i}`}>
              <div className="aspect-video overflow-hidden relative">
                <img src={getImageUrl(space, i)} alt={space.title} className="w-full h-full object-cover" />
                <Badge className={`absolute top-3 right-3 ${space.available ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"} border-0`}>{space.available ? "Disponible" : "Masque"}</Badge>
              </div>
              <div className="p-4 space-y-3">
                <div className="flex items-center justify-between"><h3 className="font-heading font-bold text-zinc-950 truncate">{space.title}</h3><span className="text-emerald-600 font-bold text-sm">{space.price_per_night?.toFixed(2)} $/nuit</span></div>
                <p className="text-sm text-zinc-500 flex items-center gap-1"><MapPin className="h-3 w-3" /> {space.city}{space.region ? `, ${space.region}` : ""}</p>
                <div className="flex items-center gap-2 text-xs text-zinc-400">
                  <span>{TERRAIN_LABELS[space.terrain_type]}</span>
                  <span>-</span>
                  <span>{space.max_spots || 1} emplacement(s)</span>
                  {space.avg_rating > 0 && <><Star className="h-3 w-3 fill-amber-400 text-amber-400 ml-auto" /><span>{space.avg_rating}</span></>}
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-zinc-100">
                  <div className="flex items-center gap-2"><Switch checked={space.available} onCheckedChange={() => toggleAvailability(space)} data-testid={`toggle-available-${i}`} /><span className="text-xs text-zinc-500">{space.available ? "Visible" : "Masque"}</span></div>
                  <div className="flex gap-1">
                    <Link to={`/spaces/${space.id}`}><Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`view-space-${i}`}><Eye className="h-4 w-4" /></Button></Link>
                    <Link to={`/edit-space/${space.id}`}><Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600" data-testid={`edit-space-${i}`}><Pencil className="h-4 w-4" /></Button></Link>
                    <Link to={`/manage-calendar/${space.id}`}><Button variant="ghost" size="icon" className="h-8 w-8 text-emerald-600" data-testid={`manage-calendar-${i}`}><CalendarDays className="h-4 w-4" /></Button></Link>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-600" onClick={() => deleteSpace(space.id)} data-testid={`delete-space-${i}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
